
import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, X, Send, BrainCircuit, Loader2, Info } from 'lucide-react';
import { getPlanningQuickAdvice } from '../services/geminiService';

const AIAssistant: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<{ role: 'user' | 'ai'; text: string }[]>([
    { role: 'ai', text: 'Hi! I am PlanStack AI. Ask me any general question about building, town planning, or consultant requirements in Australia.' }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom whenever messages change
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [messages, isLoading]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMsg = input;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setIsLoading(true);

    try {
      const advice = await getPlanningQuickAdvice(userMsg);
      if (advice) {
        setMessages(prev => [...prev, { role: 'ai', text: advice }]);
      } else {
        throw new Error("Empty response");
      }
    } catch (err) {
      setMessages(prev => [...prev, { 
        role: 'ai', 
        text: "I'm having trouble connecting to my planning engine. This usually happens during high traffic. Please try your question again in a moment." 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-[9999]">
      {isOpen ? (
        <div className="bg-white w-80 sm:w-96 h-[550px] rounded-[2rem] shadow-2xl border border-gray-200 flex flex-col overflow-hidden animate-fade-in ring-1 ring-black/5">
          {/* Header */}
          <div className="bg-brand-900 p-6 text-white flex justify-between items-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-16 -mt-16"></div>
            <div className="flex items-center gap-3 relative z-10">
              <div className="bg-accent-500 p-2 rounded-xl text-brand-900 shadow-lg">
                 <BrainCircuit size={20} />
              </div>
              <div>
                 <h3 className="font-black text-sm uppercase tracking-tight">PlanStack AI</h3>
                 <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></div>
                    <span className="text-[9px] text-brand-200 font-black uppercase tracking-widest">Planning Agent Active</span>
                 </div>
              </div>
            </div>
            <button 
              onClick={() => setIsOpen(false)} 
              className="hover:bg-white/10 p-2 rounded-full transition relative z-10"
            >
              <X size={20} />
            </button>
          </div>

          {/* Messages Container */}
          <div 
            ref={scrollRef} 
            className="flex-1 overflow-y-auto p-5 space-y-4 bg-gray-50/50 custom-scrollbar"
          >
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] p-4 rounded-2xl text-sm shadow-sm leading-relaxed ${
                  m.role === 'user' 
                    ? 'bg-brand-600 text-white rounded-tr-none' 
                    : 'bg-white text-gray-700 border border-gray-100 rounded-tl-none font-medium'
                }`}>
                  {m.text}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                 <div className="bg-white border border-gray-100 p-4 rounded-2xl rounded-tl-none shadow-sm flex items-center gap-3">
                    <Loader2 size={16} className="animate-spin text-brand-500" />
                    <span className="text-[10px] text-gray-400 font-black uppercase tracking-widest">Analysing Policy...</span>
                 </div>
              </div>
            )}
          </div>

          {/* Input Area */}
          <div className="p-4 bg-white border-t border-gray-100">
            <form onSubmit={handleSend} className="flex gap-2">
              <input 
                value={input}
                onChange={(e) => setInput(e.target.value)}
                autoFocus
                className="flex-1 bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-brand-500 outline-none transition font-medium" 
                placeholder="Ask about your project..."
                disabled={isLoading}
              />
              <button 
                type="submit" 
                disabled={isLoading || !input.trim()} 
                className="bg-brand-900 text-white p-3 rounded-xl hover:bg-black transition disabled:opacity-50 shadow-lg"
              >
                 <Send size={20} />
              </button>
            </form>
            <div className="mt-3 flex items-center justify-center gap-1.5 opacity-40">
               <Info size={10} />
               <span className="text-[8px] text-gray-600 font-black uppercase tracking-widest">Indicative Advice Only • Not Professional Counsel</span>
            </div>
          </div>
        </div>
      ) : (
        <button 
          onClick={() => setIsOpen(true)}
          className="bg-brand-900 hover:bg-black text-white p-4 rounded-full shadow-2xl flex items-center gap-3 group transition-all duration-500 hover:scale-110 active:scale-95 border-4 border-white"
        >
          <div className="max-w-0 group-hover:max-w-xs overflow-hidden transition-all duration-500 ease-in-out whitespace-nowrap">
            <span className="font-black text-xs uppercase tracking-widest pl-2 pr-1">Ask PlanStack AI</span>
          </div>
          <div className="bg-accent-500 text-brand-900 p-1.5 rounded-full shadow-inner">
            <MessageSquare size={24} />
          </div>
        </button>
      )}
    </div>
  );
};

export default AIAssistant;
